import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPlatformInvoices,
  getSubscription,
  postSubscriptionAutoRenew,
  postPlatformInvoicePay,
  getOwnerWallet,
} from '@/api/owner';
import { ShieldCheck, Lock, CheckCircle2, AlertCircle, RefreshCw, X, FileText } from 'lucide-react';

const unwrap = (r: any) => r?.data?.data ?? r?.data;
const arr = <T,>(s: any): T[] => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  if (Array.isArray(s.data)) return s.data;
  if (Array.isArray(s.data?.data)) return s.data.data;
  return [];
};
const money = (n: any) => n == null ? '—' : '₦' + Number(n).toLocaleString('en-NG', { maximumFractionDigits: 2 });
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function BillingPage() {
  const qc = useQueryClient();
  
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [pendingAction, setPendingAction] = useState<((pin: string) => void) | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error', msg: string } | null>(null);

  const [maxCap, setMaxCap] = useState<string>('');

  const invoicesQ = useQuery({ queryKey: ['owner-platform-invoices'], queryFn: () => getPlatformInvoices().then(unwrap) });
  const subQ = useQuery({ queryKey: ['owner-subscription'], queryFn: () => getSubscription().then(unwrap) });
  const walletQ = useQuery({ queryKey: ['owner-wallet'], queryFn: () => getOwnerWallet().then(unwrap) });

  const invoices = arr<any>(invoicesQ.data?.data ?? invoicesQ.data);
  const mandate = subQ.data;
  const walletBalance = walletQ.data?.balance ?? 0;

  const toggleM = useMutation({
    mutationFn: (payload: { enabled: boolean; max_amount?: number; pin: string }) => {
      return postSubscriptionAutoRenew({ enabled: payload.enabled, max_amount: payload.max_amount }, payload.pin);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner-subscription'] });
      setToast({ type: 'success', msg: 'Auto-renewal settings updated.' });
    },
    onError: (e: any) => {
      setToast({ type: 'error', msg: e?.response?.data?.message ?? 'Failed to update auto-renewal.' });
    },
  });

  const payM = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: string }) => postPlatformInvoicePay(id, pin),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner-platform-invoices'] });
      qc.invalidateQueries({ queryKey: ['owner-wallet'] });
      setToast({ type: 'success', msg: 'Invoice paid successfully.' });
    },
    onError: (e: any) => {
      setToast({ type: 'error', msg: e?.response?.data?.message ?? 'Payment failed.' });
    },
  });

  const requestPin = (action: (pin: string) => void) => {
    setPendingAction(() => action);
    setPin('');
    setPinError('');
    setShowPinModal(true);
  };

  const handleToggle = () => {
    const nextState = !mandate?.enabled;
    requestPin((pin) => {
      toggleM.mutate({ enabled: nextState, max_amount: maxCap ? Number(maxCap) : undefined, pin });
    });
  };

  const handleSaveCap = () => {
    if (!mandate?.enabled) return setToast({ type: 'error', msg: 'Turn on auto-renewal first.' });
    requestPin((pin) => {
      toggleM.mutate({ enabled: true, max_amount: maxCap ? Number(maxCap) : undefined, pin });
    });
  };

  const handlePay = (id: number) => {
    requestPin((pin) => {
      payM.mutate({ id, pin });
    });
  };

  const statusTone: Record<string, string> = {
    paid: 'bg-green-50 text-green-700 border-green-200',
    issued: 'bg-blue-50 text-blue-700 border-blue-200',
    partial: 'bg-amber-50 text-amber-800 border-amber-200',
    overdue: 'bg-red-50 text-red-700 border-red-200',
    void: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  const statusLabel: Record<string, string> = { issued: 'Unpaid', partial: 'Part paid', overdue: 'Overdue', paid: 'Paid', void: 'Cancelled', written_off: 'Written off' };
  const kindLabel: Record<string, string> = { renewal: 'Subscription', top_up: 'New students', sms: 'SMS used', ad_hoc: 'OJP bill' };
  const owing = invoices.filter((i: any) => i.can_pay ?? (['issued', 'partial', 'overdue'].includes(i.status) && Number(i.balance) > 0));
  const owingTotal = owing.reduce((t: number, i: any) => t + Number(i.balance || 0), 0);
  const paidTotal = invoices.reduce((t: number, i: any) => t + Number(i.amount_paid || 0), 0);
  const [openInv, setOpenInv] = useState<number | null>(null);

  return (
    <div className="max-w-[1000px] mx-auto p-4 sm:p-6 space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 left-4 sm:left-auto z-[100] sm:max-w-md p-4 rounded-xl shadow-2xl flex items-start gap-3 text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
          <p className="text-sm font-semibold flex-1">{toast.msg}</p>
          <button onClick={() => setToast(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      <div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">FINANCE</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Billing & Auto-renew</h1>
        <p className="text-slate-500 text-sm mt-1">What OJP has billed your school, what is left to pay, and automatic payment.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-[#0f172a] rounded-2xl p-5 text-white shadow-lg">
          <p className="text-slate-400 text-xs font-semibold">School wallet</p>
          <p className="text-3xl font-extrabold tracking-tight mt-1">{money(walletBalance)}</p>
          <p className="text-xs mt-2 text-slate-400">{walletQ.data?.status === 'active' ? 'Active' : 'Not active yet'}</p>
        </div>
        <div className={`rounded-2xl p-5 border ${owingTotal > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
          <p className="text-slate-500 text-xs font-semibold">Left to pay OJP</p>
          <p className={`text-3xl font-extrabold tracking-tight mt-1 ${owingTotal > 0 ? 'text-amber-800' : 'text-slate-900'}`}>{money(owingTotal)}</p>
          <p className="text-xs mt-2 text-slate-500">{owing.length ? `${owing.length} invoice${owing.length > 1 ? 's' : ''} waiting` : 'Nothing waiting'}</p>
        </div>
        <div className="rounded-2xl p-5 border bg-white border-slate-200">
          <p className="text-slate-500 text-xs font-semibold">Paid to OJP so far</p>
          <p className="text-3xl font-extrabold tracking-tight mt-1 text-slate-900">{money(paidTotal)}</p>
          <p className="text-xs mt-2 text-slate-500 flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${mandate?.enabled ? 'bg-green-500' : 'bg-slate-400'}`} />Automatic payment {mandate?.enabled ? 'on' : 'off'}</p>
        </div>
      </div>

      {owing.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2"><Lock className="w-4 h-4 text-amber-600" />{mandate?.enabled ? 'Coming up' : 'Waiting for your approval'}</h2>
          {owing.map((inv: any) => {
            const on = inv.debit_on ?? inv.due_date;
            const auto = inv.auto_debit ?? mandate?.enabled;
            return (
              <div key={inv.id} className="bg-white rounded-2xl border border-amber-200 shadow-sm p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-slate-500 uppercase">{kindLabel[inv.kind] ?? 'OJP bill'} · <span className="font-mono">{inv.number}</span></p>
                    <p className="text-lg font-bold text-slate-900 mt-0.5">{inv.period_label}</p>
                    <p className="text-sm text-slate-600 mt-1">{inv.billable_students ? `${inv.billable_students} student${inv.billable_students > 1 ? 's' : ''} · ` : ''}{auto ? <>Taken from your wallet automatically on <b>{fmtDate(on)}</b></> : <>Due <b>{fmtDate(on)}</b></>}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-extrabold text-slate-900">{money(inv.balance)}</p>
                    {Number(inv.amount_paid) > 0 && <p className="text-xs text-slate-500">of {money(inv.total)} · {money(inv.amount_paid)} paid</p>}
                  </div>
                </div>
                {Array.isArray(inv.items) && inv.items.length > 0 && (
                  <ul className="mt-3 rounded-xl bg-slate-50 divide-y divide-slate-200/70 text-sm">
                    {inv.items.map((it: any, k: number) => <li key={k} className="flex justify-between gap-3 px-3 py-2"><span className="text-slate-600">{it.description}</span><b className="shrink-0">{money(it.amount)}</b></li>)}
                  </ul>
                )}
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button onClick={() => handlePay(inv.id)} disabled={payM.isPending} className="px-5 py-2.5 bg-[#0f172a] text-white rounded-xl text-sm font-bold hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />{auto ? `Pay ${money(inv.balance)} now` : `Approve & pay ${money(inv.balance)}`}
                  </button>
                  <p className="text-xs text-slate-500">{Number(walletBalance) < Number(inv.balance) ? <span className="text-red-600 font-semibold">Your wallet is short by {money(Number(inv.balance) - Number(walletBalance))}. Top it up first.</span> : 'Confirmed with your transaction PIN and paid from your wallet at once.'}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" /> All OJP invoices
          </h2>
          <button onClick={() => invoicesQ.refetch()} className="text-sm font-semibold text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
        {invoicesQ.isLoading ? (
          <p className="p-8 text-center text-slate-500">Loading invoices…</p>
        ) : invoices.length === 0 ? (
          <p className="p-8 text-center text-slate-500">No invoices yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {invoices.map((inv: any) => (
              <li key={inv.id}>
                <button onClick={() => setOpenInv(openInv === inv.id ? null : inv.id)} className="w-full text-left px-4 sm:px-5 py-3.5 hover:bg-slate-50 grid grid-cols-[1fr_auto] sm:grid-cols-[1.4fr_1fr_auto_auto] gap-x-4 gap-y-1 items-center">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{kindLabel[inv.kind] ?? 'OJP bill'} <span className="font-mono text-xs text-slate-500">{inv.number}</span></p>
                    <p className="text-xs text-slate-500 truncate">{inv.period_label}{inv.billable_students ? ` · ${inv.billable_students} student${inv.billable_students > 1 ? 's' : ''}` : ''}</p>
                  </div>
                  <div className="text-right sm:text-left">
                    <p className="font-bold text-slate-900">{money(inv.total)}</p>
                    <p className="text-xs text-slate-500">{Number(inv.balance) > 0 ? `${money(inv.balance)} left` : `paid ${money(inv.amount_paid)}`}</p>
                  </div>
                  <p className="hidden sm:block text-xs text-slate-500">Due {fmtDate(inv.due_date)}</p>
                  <span className={`justify-self-end text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${statusTone[inv.status] ?? 'bg-slate-100'}`}>{statusLabel[inv.status] ?? inv.status}</span>
                </button>
                {openInv === inv.id && (
                  <div className="px-4 sm:px-5 pb-4">
                    <ul className="rounded-xl bg-slate-50 divide-y divide-slate-200/70 text-sm">
                      {(inv.items ?? []).map((it: any, k: number) => <li key={k} className="flex justify-between gap-3 px-3 py-2"><span className="text-slate-600">{it.description}</span><b className="shrink-0">{money(it.amount)}</b></li>)}
                      {Number(inv.credits_applied) > 0 && <li className="flex justify-between px-3 py-2 text-green-700"><span>Credit applied</span><b>−{money(inv.credits_applied)}</b></li>}
                      <li className="flex justify-between px-3 py-2"><span className="font-semibold">Total</span><b>{money(inv.total)}</b></li>
                      <li className="flex justify-between px-3 py-2"><span>Paid</span><b className="text-green-700">{money(inv.amount_paid)}</b></li>
                      <li className="flex justify-between px-3 py-2"><span>Left to pay</span><b>{money(inv.balance)}</b></li>
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" /> Auto-renewal with my school wallet
            </h2>
            <p className="text-sm text-slate-500 mt-1 max-w-lg">
              With this on, OJP takes each bill from your school wallet on the day it is due, and tells you a few days before. With it off, you approve every bill yourself with your transaction PIN.
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={toggleM.isPending}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm shadow-sm transition-colors ${
              mandate?.enabled ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100' : 'bg-[#0f172a] text-white hover:bg-slate-800'
            }`}
          >
            {mandate?.enabled ? 'Turn off auto-renewal' : 'Turn on auto-renewal'}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">Status</p>
            <p className="font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${mandate?.enabled ? 'bg-green-500' : 'bg-slate-400'}`} />
              {mandate?.enabled ? 'On' : 'Off'}
            </p>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">Last Run</p>
            <p className="font-semibold text-slate-900 mt-1">{mandate?.last_run_at ? fmtDate(mandate.last_run_at) : 'Never'}</p>
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">Failed Debits</p>
            <p className="font-semibold text-slate-900 mt-1">{mandate?.failure_count ?? 0}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-slate-100">
          <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Max charge per renewal (Safety Cap)</label>
          <div className="flex gap-2 max-w-md">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">₦</span>
              <input
                type="number"
                value={maxCap}
                onChange={(e) => setMaxCap(e.target.value)}
                placeholder="e.g. 200000"
                className="w-full pl-8 pr-3 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
              />
            </div>
            <button
              onClick={handleSaveCap}
              disabled={!mandate?.enabled || toggleM.isPending}
              className="px-4 py-2.5 bg-slate-100 text-slate-700 border border-slate-300 rounded-xl font-bold text-sm hover:bg-slate-200 disabled:opacity-50"
            >
              Save cap
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">If an invoice exceeds this amount, we'll skip it and wait for you to pay manually. Leave blank for no cap.</p>
        </div>
      </div>

      {showPinModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Authorize Action</h3>
              <p className="text-sm text-slate-500 mb-6">Enter your 4-digit security PIN to confirm.</p>
              
              <input
                type="password"
                maxLength={4}
                value={pin}
                onChange={(e) => { setPin(e.target.value.replace(/\D/g, '')); setPinError(''); }}
                className="w-full border-2 border-slate-300 rounded-xl p-3 text-center text-2xl tracking-[0.5em] font-mono focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                placeholder="••••"
                autoFocus
              />
              {pinError && <p className="text-xs text-red-500 font-semibold mt-2">{pinError}</p>}
            </div>
            
            <div className="bg-slate-50 p-4 flex gap-3 border-t border-slate-200">
              <button
                onClick={() => { setShowPinModal(false); setPin(''); setPendingAction(null); }}
                className="flex-1 py-2.5 bg-white border border-slate-300 rounded-xl font-bold text-sm text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (pin.length !== 4) return setPinError('PIN must be 4 digits');
                  if (pendingAction) pendingAction(pin);
                  setShowPinModal(false); setPin(''); setPendingAction(null);
                }}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-xl font-bold text-sm hover:bg-slate-800"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
